const GENERIC_MESSAGE = 'An unexpected error occurred. Please contact a developer';
const NETWORK_MESSAGE = 'Could not reach the server. Check your connection and try again.';
const ACCOUNT_MESSAGE = 'Could not load your account details, so nothing was saved. Reload the page and try again.';

// Synthetic status used by useCreateUpdateProject when it declines to send a request
// because the current user is unknown. Not an HTTP status - there was no request.
export const ACCOUNT_UNAVAILABLE = 'ACCOUNT_UNAVAILABLE';

// Mirrors the wizard's on-screen labels so a backend rejection names the field the way
// the user saw it. Unknown keys fall back to title-casing.
const FIELD_LABELS: Record<string, string> = {
  // Project Information
  firm: 'Firm Name',
  name: 'Project Name',
  address: 'Address',
  country: 'Country',
  subnational: 'State / Province / Region',
  city: 'City',
  zipcode: 'Zip Code',
  primary_building_use_type: 'Primary Building Use Type',
  construction_type: 'Construction Type',
  construction_completion_year: 'Construction Completion Year',
  gross_area_sqft: 'Gross Area (ft²)',
  mean_roof_height_ft: 'Mean Roof Height (ft)',
  stories_above_grade: 'Number of Stories Above Grade',
  stories_below_grade: 'Number of Stories Below Grade',

  // Structural System
  tcg_long_direction_ft: 'Typical Column Grid, Long Direction (ft)',
  risk_category: 'Risk Category',
  typical_floor_live_load_psf: 'Typical Floor Live Load (psf)',
  ground_snow_load_psf: 'Ground Snow Load (psf)',
  ultimate_wind_speed_mph: 'Ultimate Wind Speed (mph)',
  seismic_design_category: 'Seismic Design Category',
  primary_horizontal_gravity_system: 'Primary Horizontal Gravity System',
  primary_vertical_gravity_system: 'Primary Vertical Gravity System',
  primary_lateral_system: 'Primary Lateral System',
  podium: 'Podium',
  foundation_type: 'Foundation Type',
  allowable_soil_bearing_pressure_psf: 'Allowable Soil Bearing Pressure (psf)',

  // Structural Material Quantity assumptions
  material_quantity_data_source: 'Material Quantity Data Source',
  steel_connection_allowances: 'Steel connection allowances',
  rebar_allowances: 'Rebar allowances',
  concrete_waste_allowances: 'Concrete waste allowances',
  carbon_factor_data_source: 'Carbon Factor Data Source',
  other_carbon_factor_data_source: 'Specify Other Carbon Factor Data Source',
  additional_assumptions: 'Additional Information',

  // Embodied Carbon Assessment
  lca_tool: 'Embodied Carbon Assessment Tool',
  other_lca_tool: 'Third Party Embodied Carbon Assessment Tool',
  lca_year: 'Year Third Party Assessment was Performed',
  lca_project_phase: 'Project Phase',
  ref_study_years: 'Reference Study Period',

  // Embodied Carbon Reporting
  a1_a3_gwp_kg: 'A1-A3 (kgCO2e)',
  a4_gwp_kg: 'A4 (kgCO2e)',
  a5_gwp_kg: 'A5 (kgCO2e)',
  b1_b5_gwp_kg: 'B1-B5 (kgCO2e)',
  c1_c4_gwp_kg: 'C1-C4 (kgCO2e)',
  d_gwp_kg: 'D (kgCO2e)',
  biogenic_carbon_flow_kg: 'Biogenic Carbon Flow (kgCO2e)',
};

// Keys inside a `smqs` row. Kept separate because these names are generic
// ("field", "quantity") and would be misleading as top-level labels.
const SMQ_FIELD_LABELS: Record<string, string> = {
  field: 'Structure',
  material_type: 'Material Type',
  quantity: 'Quantity',
  is_lateral: 'Lateral Element',
  a1_a3_carbon_factor_override: 'Carbon Factor Override',
  // Only reachable from the upload guard's blank-project-name row_errors, not from a
  // real StructureMaterialQuantitiesSerializer error - the model field is `project`.
  project: 'Project Name',
};

const titleCase = (field: string): string => {
  const label = field
    .split('_')
    .map(word => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');

  return label;
};

const fieldLabel = (field: string): string => {
  const label = FIELD_LABELS[field] ?? titleCase(field);

  return label;
};

const smqFieldLabel = (field: string): string => {
  const label = SMQ_FIELD_LABELS[field] ?? fieldLabel(field);

  return label;
};

// DRF nests validation errors arbitrarily deep (lists of strings, dicts of lists,
// lists of dicts). Collect every leaf string.
const flattenMessages = (value: unknown): string[] => {
  if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)];
  if (Array.isArray(value)) return value.flatMap(flattenMessages);
  if (value && typeof value === 'object') return Object.values(value).flatMap(flattenMessages);
  return [];
};

// Some messages name other fields mid-sentence ("... is a required field when
// lca_tool is 'Other'."). Swap any snake_case token we have a label for. Requires an
// underscore so ordinary prose is never touched.
const humanizeEmbeddedFieldNames = (message: string): string => {
  const humanized = message.replace(
    /[a-z][a-z0-9]*(?:_[a-z0-9]+)+/g,
    match => FIELD_LABELS[match] ?? match
  );

  return humanized;
};

// `validate()` emits messages that already start with the raw field name
// ("zipcode is a required field."). Swap in the friendly label rather than
// prefixing it and repeating the name twice.
const describeFieldError = (field: string, label: string, message: string): string => {
  const namesItsOwnField = message.startsWith(field);
  const body = namesItsOwnField ? `${label}${message.slice(field.length)}` : message;
  const humanized = humanizeEmbeddedFieldNames(body);
  const described = namesItsOwnField ? humanized : `${label}: ${humanized}`;

  return described;
};

const describeSmqErrors = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    const wholeFieldMessages = flattenMessages(value).map(message => `SMQ: ${message}`);

    return wholeFieldMessages;
  }

  const rowMessages = value.flatMap((row, index) => {
    const rowLabel = `SMQ row ${index + 1}`;

    if (row && typeof row === 'object' && !Array.isArray(row)) {
      const fieldMessages = Object.entries(row).flatMap(([field, fieldValue]) =>
        flattenMessages(fieldValue).map(
          message => `${rowLabel} - ${describeSmqFieldError(field, message)}`
        )
      );

      return fieldMessages;
    }

    const plainMessages = flattenMessages(row).map(message => `${rowLabel}: ${message}`);

    return plainMessages;
  });

  return rowMessages;
};

const describeSmqFieldError = (field: string, message: string): string => {
  const described = describeFieldError(field, smqFieldLabel(field), message);

  return described;
};

// The bulk-upload endpoint validates every row in one call, so a DRF error is keyed by
// list position rather than by sheet + row - `views.py` re-attaches both before
// responding, as `{ row_errors: [{ sheet, row, errors }] }`.
const rowErrorMessages = (errors: unknown, isSmqRow: boolean): string[] =>
  Object.entries(errors ?? {}).flatMap(([field, value]) =>
    flattenMessages(value).map(message =>
      isSmqRow ? describeSmqFieldError(field, message) : describeFieldError(field, fieldLabel(field), message)
    )
  );

const describeRowError = (entry: any): string[] => {
  const { sheet, row, errors } = entry ?? {};
  const rowLabel = row != null ? `${sheet} row ${row}` : sheet;

  return rowErrorMessages(errors, sheet === 'Structural Material Quantities').map(
    message => `${rowLabel} - ${message}`
  );
};

const describeRowErrors = (rowErrors: unknown): string[] =>
  Array.isArray(rowErrors) ? rowErrors.flatMap(describeRowError) : [];

export interface UploadRowError {
  sheet: string;
  row: number | null;
  message: string;
}

// Structured counterpart to describeRowErrors, for callers that render a table instead
// of a flat string (e.g. UploadButton) - one entry per field violation, sheet and row
// kept as their own values rather than baked into a label string.
export const getUploadRowErrors = (error: any): UploadRowError[] | null => {
  const rowErrors = error?.data?.row_errors;
  if (!Array.isArray(rowErrors)) return null;

  return rowErrors.flatMap((entry: any) => {
    const { sheet, row, errors } = entry ?? {};

    return rowErrorMessages(errors, sheet === 'Structural Material Quantities').map(message => ({
      sheet,
      row: row ?? null,
      message,
    }));
  });
};

const SUMMARY_KEYS = ['detail', 'message', 'error'] as const;
const UNLABELLED_KEYS = ['non_field_errors', 'detail', 'message', 'error'];

export const getErrorMessage = (error: any): string => {
  if (!error?.status) return GENERIC_MESSAGE;

  // RTK Query transport failures report a string status instead of an HTTP code.
  if (typeof error.status === 'string') {
    if (error.status === ACCOUNT_UNAVAILABLE) return ACCOUNT_MESSAGE;

    const isNetworkFailure = error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR';

    return isNetworkFailure ? NETWORK_MESSAGE : GENERIC_MESSAGE;
  }

  const prefix = `Error ${error.status}: `;
  const { data } = error;

  if (!data) return GENERIC_MESSAGE;

  // Django's debug/500 pages come back as HTML - never worth showing a user.
  if (typeof data === 'string') {
    const body = data.trim();
    const isUnusable = !body || body.startsWith('<');

    return isUnusable ? GENERIC_MESSAGE : `${prefix}${body}`;
  }

  if (typeof data !== 'object') return GENERIC_MESSAGE;

  // A single summary message wins, matching the previous behaviour.
  for (const key of SUMMARY_KEYS) {
    const [summary] = flattenMessages(data[key]);
    if (summary) return `${prefix}${summary}`;
  }

  if (Array.isArray(data.row_errors)) {
    const rowMessages = describeRowErrors(data.row_errors);

    // A bulk upload can reject dozens of rows - one per line, not one long "; "-joined
    // run. UploadButton renders the structured `getUploadRowErrors` as a table instead
    // of this string, but keep the "\n" join here too for any other caller.
    return rowMessages.length ? `${prefix}${rowMessages.join('\n')}` : GENERIC_MESSAGE;
  }

  const messages = Object.entries(data).flatMap(([field, value]) => {
    if (field === 'smqs') return describeSmqErrors(value);
    if (UNLABELLED_KEYS.includes(field)) return flattenMessages(value);

    const label = fieldLabel(field);
    const fieldMessages = flattenMessages(value).map(
      message => describeFieldError(field, label, message)
    );

    return fieldMessages;
  });

  const message = messages.length ? `${prefix}${messages.join('; ')}` : GENERIC_MESSAGE;

  return message;
};

// The top-level fields a validation response rejected, so the caller can drop those
// values rather than resending them on every later save (ID 7 / ID 9). `smqs` is
// deliberately excluded - clearing it would discard the quantities entered on steps 3-5.
export const getRejectedFieldNames = (error: any): string[] => {
  const { data } = error ?? {};

  if (typeof error?.status !== 'number' || !data || typeof data !== 'object') return [];

  const fields = Object.keys(data).filter(
    field => field !== 'smqs' && !UNLABELLED_KEYS.includes(field)
  );

  return fields;
};
